import { useMemo } from 'react';
import { instantFrom, nowInstant } from '@insio/dates';
import { TimeZone } from '@insio/shared-types';
import { Select } from './Select';
import { useLocale, useTranslation } from '../../providers/UIProvider';
import type { GlobalSize, SelectInputOption } from '../../types';
import type { TestIdProps } from '../../utils';

/** Local-market zones shown first inside their merged group. */
const PREFERRED = ['Europe/Prague', 'Europe/Bratislava', 'Europe/Vienna'];

/** English city from an IANA id — locale-independent ordering / search key. */
function englishCity(ianaId: string): string {
  if (ianaId === 'UTC') return 'Coordinated Universal Time';
  return (ianaId.split('/').pop() ?? ianaId).replace(/_/g, ' ');
}

/**
 * Zones sharing a DST signature (offset in January + July) keep the same wall
 * clock all year, so they collapse into one option. Grouping is locale- and
 * time-independent, so it's computed once. Each group is ordered preferred-first
 * then by English city name; `[0]` is the representative stored value.
 */
const ZONE_GROUPS: readonly (readonly string[])[] = (() => {
  const jan = instantFrom('2025-01-15T12:00:00Z');
  const jul = instantFrom('2025-07-15T12:00:00Z');
  const bySignature = new Map<string, string[]>();
  for (const tz of Object.values(TimeZone) as string[]) {
    const signature = `${jan.toZonedDateTimeISO(tz).offsetNanoseconds}|${jul.toZonedDateTimeISO(tz).offsetNanoseconds}`;
    const zones = bySignature.get(signature);
    if (zones) zones.push(tz);
    else bySignature.set(signature, [tz]);
  }
  return [...bySignature.values()].map(zones =>
    [...zones].sort((a, b) => {
      const pa = PREFERRED.indexOf(a);
      const pb = PREFERRED.indexOf(b);
      if (pa !== -1 || pb !== -1) return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
      return englishCity(a).localeCompare(englishCity(b));
    })
  );
})();

/** Any zone → the representative (stored) zone of its group. */
const REPRESENTATIVE = new Map<string, string>(
  ZONE_GROUPS.flatMap(zones => zones.map(tz => [tz, zones[0]] as const))
);

export interface TimezoneSelectProps extends TestIdProps {
  id?: string;
  /** IANA time zone identifier (e.g. `Europe/Prague`); empty/null clears the selection. */
  value: string | null | undefined;
  /** Called with the selected IANA identifier (or `''` when the unselected option is picked). */
  onChange: (timeZone: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Open the dropdown on mount — useful when used as an inline editor. */
  defaultOpen?: boolean;
  /** Show a search field (magnifying glass) instead of a chevron dropdown. @default false */
  isSearchable?: boolean;
  height?: GlobalSize;
  className?: string;
  hasError?: boolean;
  /** Label for an extra "no selection" row at the top of the list. */
  unselectedOptionLabel?: string;
}

/**
 * Dropdown for IANA time zone identifiers. Zones with identical year-round
 * behaviour are merged into one Outlook-style option — `(UTC±HH:MM) City1,
 * City2, City3 +N` — sorted by UTC offset. The stored value is the group's
 * representative zone.
 */
export function TimezoneSelect({
  id,
  testId,
  value,
  onChange,
  onBlur,
  disabled,
  autoFocus,
  defaultOpen,
  isSearchable = false,
  height,
  className,
  hasError,
  unselectedOptionLabel
}: TimezoneSelectProps) {
  const t = useTranslation('components.timezoneSelect');
  const locale = useLocale();

  const options = useMemo<SelectInputOption[]>(() => {
    const now = nowInstant();
    return ZONE_GROUPS.map(zones => {
      const zoned = now.toZonedDateTimeISO(zones[0]);
      const names = zones.map(tz => t(`city.${tz}`));
      const extra = names.length - 3;
      const option: SelectInputOption = {
        value: zones[0],
        label: `(UTC${zoned.offset}) ${names.slice(0, 3).join(', ')}${extra > 0 ? ` +${extra}` : ''}`
      };
      return { option, offsetNs: zoned.offsetNanoseconds };
    })
      .sort(
        (a, b) => a.offsetNs - b.offsetNs || a.option.label.localeCompare(b.option.label, locale)
      )
      .map(entry => entry.option);
    // Rebuilt only when the UI language changes; `t` is stable per language.
  }, [locale]);

  const current = value == null ? '' : (REPRESENTATIVE.get(String(value)) ?? String(value));

  return (
    <Select
      id={id}
      testId={testId}
      isSearchable={isSearchable}
      defaultOpen={defaultOpen}
      options={options}
      value={current}
      UnselectedOption={unselectedOptionLabel}
      onChange={next => {
        const picked = Array.isArray(next) ? next[0] : next;
        onChange(picked == null ? '' : String(picked));
      }}
      onBlur={onBlur}
      autoFocus={autoFocus}
      disabled={disabled}
      height={height}
      className={className}
      hasError={hasError}
    />
  );
}

TimezoneSelect.displayName = 'TimezoneSelect';
